// Settings › Backup & Restore (owner only). Download a copy of the whole
// company database, or put the company back to an earlier copy. The work is
// in utils/backup.ts; this is the screen and the safety steps around it.

import { useEffect, useRef, useState } from 'react';
import { writeBatch } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Button, Modal } from './ui';
import { logActivity, actorName } from '../utils/activity';
import {
  createBackup, downloadBackup, readBackupFile, restoreBackup,
  setRestoreLock, clearRestoreLock, watchRestoreLock, saveLastBackup, watchLastBackup,
  type BackupFile, type RestoreLock, type LastBackup,
} from '../utils/backup';

const fmtDate = (iso: string) => new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
function daysAgo(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
}
const total = (f: BackupFile) => f.docs.length;

// The figures a person recognises, in the restore summary.
const SUMMARY: { key: string; label: string }[] = [
  { key: 'invoices', label: 'Purchases' }, { key: 'sales', label: 'Sales' },
  { key: 'inventory', label: 'Stock items' }, { key: 'entities', label: 'Companies' },
  { key: 'bankTransactions', label: 'Bank entries' }, { key: 'journalEntries', label: 'Ledger entries' },
];

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="set-section">
      <div className="set-section-head"><h4>{title}</h4>{note && <p>{note}</p>}</div>
      <div className="set-section-body">{children}</div>
    </section>
  );
}

function Bar({ done, of }: { done: number; of: number }) {
  const pct = of > 0 ? Math.round((done / of) * 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-2)', overflow: 'hidden', margin: '8px 0 4px', maxWidth: 440 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.2s' }} />
    </div>
  );
}

type Phase = 'idle' | 'safety' | 'write' | 'done' | 'failed';

export default function BackupPanel() {
  const { business, user } = useAuth();
  const db = getClientDb();
  const meta = { businessId: business?.id ?? '', businessName: business?.businessName ?? '', userName: actorName(user) };

  const [last, setLast] = useState<LastBackup | null>(null);
  const [lock, setLock] = useState<RestoreLock | null>(null);
  useEffect(() => {
    if (!db) return;
    const a = watchLastBackup(db, setLast);
    const b = watchRestoreLock(db, setLock);
    return () => { a(); b(); };
  }, [db]);

  // ── Backup ──
  const [backingUp, setBackingUp] = useState(false);
  const [backupStep, setBackupStep] = useState({ done: 0, total: 0 });
  const [backupMsg, setBackupMsg] = useState('');
  const [backupErr, setBackupErr] = useState('');

  async function runBackup() {
    if (!db || !business) return;
    setBackingUp(true); setBackupErr(''); setBackupMsg('');
    try {
      const file = await createBackup(db, meta, p => setBackupStep({ done: p.done, total: p.total }));
      downloadBackup(file);
      const w = writeBatch(db);
      logActivity(w, db, business.id, user, { module: 'data', action: 'backup', itemCount: total(file), note: 'Downloaded a backup' });
      await Promise.all([saveLastBackup(db, { at: file.createdAt, byName: meta.userName, docCount: total(file) }), w.commit()]);
      setBackupMsg(`Saved ${total(file).toLocaleString()} records to your Downloads folder.`);
    } catch (e) {
      console.error('[Backup] failed:', e);
      setBackupErr(e instanceof Error ? e.message : 'The backup could not be made.');
    }
    setBackingUp(false);
  }

  // ── Restore ──
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<BackupFile | null>(null);
  const [fileErr, setFileErr] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [step, setStep] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<{ removed: number; written: number } | null>(null);
  const [restoreErr, setRestoreErr] = useState('');
  const running = phase === 'safety' || phase === 'write';

  // Leaving the page mid restore would stop it half way.
  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  async function pickFile(f: File) {
    setFileErr(''); setFile(null); setPhase('idle'); setResult(null); setRestoreErr('');
    try { setFile(await readBackupFile(f, business?.id ?? '')); }
    catch (e) { setFileErr(e instanceof Error ? e.message : 'This file can\'t be used.'); }
  }

  async function runRestore() {
    if (!db || !business || !user || !file) return;
    setConfirmOpen(false); setTyped(''); setRestoreErr(''); setResult(null);
    try {
      // 1. Pause everyone else.
      await setRestoreLock(db, { active: true, byId: user.id, byName: meta.userName, startedAt: new Date().toISOString(), backupDate: file.createdAt });
      // 2. Safety copy of today's data, downloaded before anything changes.
      setPhase('safety'); setStep({ done: 0, total: 0 });
      const safety = await createBackup(db, meta, p => setStep({ done: p.done, total: p.total }));
      downloadBackup(safety);
      // 3. Put the backup back.
      setPhase('write'); setStep({ done: 0, total: 0 });
      const r = await restoreBackup(db, file, safety, p => setStep({ done: p.done, total: p.total }));
      // 4. Note it in the activity log (after the log itself was restored).
      const w = writeBatch(db);
      logActivity(w, db, business.id, user, {
        module: 'data', action: 'restore', itemCount: r.written,
        note: `Backup of ${fmtDate(file.createdAt)}`,
      });
      await w.commit();
      await clearRestoreLock(db);
      setResult(r); setPhase('done');
    } catch (e) {
      console.error('[Restore] failed:', e);
      setRestoreErr(e instanceof Error ? e.message : 'The restore stopped.');
      setPhase('failed');   // the lock stays on until it's finished or unlocked
    }
  }

  const lockedByMeElsewhere = lock && lock.byId === user?.id && !running && phase !== 'done';
  const nameOk = typed.trim().toLowerCase() === (business?.businessName ?? '').trim().toLowerCase();
  const muted = { fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6 } as const;

  return (
    <div>
      <div className="card-title" style={{ marginBottom: 4 }}>Backup &amp; Restore</div>

      {lockedByMeElsewhere && (
        <div className="msg msg-error" style={{ margin: '8px 0 4px', maxWidth: 680 }}>
          A restore you started on {fmtDate(lock.startedAt)} did not finish, so the app is paused for everyone.
          Choose the same backup file below and restore it again to finish, or{' '}
          <button type="button" className="line-item-link-btn" onClick={() => db && clearRestoreLock(db)}>unpause the app</button>.
        </div>
      )}

      <Section title="Backup" note="A copy of all your company data">
        <div style={muted}>
          {last
            ? <>Last backup: <strong style={{ color: 'var(--text-2)' }}>{daysAgo(last.at)}</strong> ({fmtDate(last.at)}, by {last.byName})</>
            : 'No backup yet.'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={runBackup} loading={backingUp} disabled={running}>Download Backup</Button>
          {backingUp && <span style={muted}>Reading your data ({backupStep.done} of {backupStep.total})</span>}
        </div>
        {backupMsg && <div style={{ ...muted, color: 'var(--success)', marginTop: 8 }}>{backupMsg}</div>}
        {backupErr && <div className="msg msg-error" style={{ marginTop: 8, maxWidth: 680 }}>{backupErr}</div>}
        <div className="msg-note" style={{ marginTop: 12 }}>
          The file holds all your customers, prices, costs and bank details. Keep it somewhere safe and don't send it to anyone.
        </div>
      </Section>

      <Section title="Restore" note="Go back to a backup">
        <div style={{ ...muted, maxWidth: 680 }}>
          Your company goes back to exactly how it was when the backup was made. Anything added or changed after that is removed.
          A copy of today's data downloads first, so you can undo.
        </div>
        <input ref={fileRef} type="file" accept=".json,application/json" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pickFile(f); }} />
        <div style={{ marginTop: 10 }}>
          <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={running}>Choose Backup File</Button>
        </div>
        {fileErr && <div className="msg msg-error" style={{ marginTop: 8, maxWidth: 680 }}>{fileErr}</div>}

        {file && phase !== 'done' && (
          <div style={{ marginTop: 12, border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '12px 14px', maxWidth: 560 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>Backup of {fmtDate(file.createdAt)}</div>
            <div style={{ ...muted, marginBottom: 8 }}>{file.businessName} · made by {file.createdBy} · {total(file).toLocaleString()} records</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 12px' }}>
              {SUMMARY.map(s => (
                <div key={s.key}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{s.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{(file.counts[s.key] ?? 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
            {!running && (
              <div style={{ marginTop: 12 }}>
                <Button variant="danger" onClick={() => setConfirmOpen(true)}>Restore This Backup</Button>
              </div>
            )}
          </div>
        )}

        {running && (
          <div style={{ marginTop: 12, maxWidth: 560 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
              {phase === 'safety' ? 'Step 1 of 2: saving a copy of today\'s data' : 'Step 2 of 2: putting the backup back'}
            </div>
            <Bar done={step.done} of={step.total} />
            <div style={muted}>Keep this page open. Other users see a "please wait" screen until it's done.</div>
          </div>
        )}

        {phase === 'failed' && (
          <div className="msg msg-error" style={{ marginTop: 12, maxWidth: 680 }}>
            The restore stopped: {restoreErr} Your data may be half restored. Click Restore This Backup again to finish it.
          </div>
        )}

        {phase === 'done' && result && (
          <div style={{ marginTop: 12, maxWidth: 560 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--success)' }}>Restore finished</div>
            <div style={muted}>
              {result.written.toLocaleString()} records put back, {result.removed.toLocaleString()} newer records removed.
              The copy of today's data is in your Downloads folder.
            </div>
            <div style={{ marginTop: 10 }}>
              <Button variant="primary" onClick={() => window.location.reload()}>Reload the App</Button>
            </div>
          </div>
        )}
      </Section>

      <Modal
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setTyped(''); }}
        title="Restore this backup?"
        size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => { setConfirmOpen(false); setTyped(''); }}>Cancel</Button>
          <Button variant="danger" disabled={!nameOk} onClick={runRestore}>Restore</Button>
        </>}
      >
        {file && (
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              {business?.businessName} goes back to <strong>{fmtDate(file.createdAt)}</strong>.
              Everything added or changed after that is removed for every user.
            </p>
            <p>A copy of today's data downloads first. Type the company name to confirm.</p>
            <input
              value={typed} onChange={e => setTyped(e.target.value)} placeholder={business?.businessName}
              autoFocus style={{ width: '100%', boxSizing: 'border-box', height: 36 }}
              onKeyDown={e => { if (e.key === 'Enter' && nameOk) runRestore(); }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
